import React, { useMemo, useState } from 'react';
import tinycolor from 'tinycolor2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Button from 'components/shared/Button';
import Alert from 'components/shared/Alert';

import CartProductImage from './CartProductImage';
import CartProductQuantityInput from './CartProductQuantityInput';
import CartProductLineItem from './CartProductLineItem';

import { formatPrice } from 'utils/common';

import type { CartProductMaybeThumb } from 'domains/cart_products';
import type { CartProductLineItemProps } from './CartProductLineItem';

type CartProductProps = {
  allowEdit: boolean;
  index: number;
  product: CartProductMaybeThumb;
  onShowImageGallery: (show: boolean, index: number) => void;
  changeCartProductQuantity: (id: number, quantity: number) => void;
  onEdit?: (product: CartProductMaybeThumb) => void;
  onDelete?: (id: number) => void;
};

function CartProductItem({
  product,
  index,
  allowEdit,
  onShowImageGallery,
  changeCartProductQuantity = () => {},
  onDelete = () => {},
  onEdit = () => {},
}: CartProductProps) {
  const { id, quantity } = product;

  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const borderColor = useMemo(() => {
    return product.border_color ? tinycolor(product.border_color).toRgbString() : undefined;
  }, [product]);

  const lineItems = useMemo(() => {
    const items: CartProductLineItemProps[] = product.options
      .filter((option) => !option.hidden)
      .map((option) => ({ price: option.pricing.total, label: option.name }));

    if (product.pricing.unit_price_before_options) {
      items.unshift({ price: product.pricing.unit_price_before_options, label: 'Base Price' });
    }

    return items.map((item) => <CartProductLineItem key={item.label} {...item} />);
  }, [product]);

  const photoFilenames = useMemo(
    () => product.photos.map((p) => ({ id: p.id, filename: p.filename })),
    [product]
  );

  const productTitle = useMemo(() => {
    const { height, width } = product.custom_size;

    if (height && width) {
      return `${width}x${height} - ${product.name}`;
    }

    return product.name;
  }, [product]);

  function handleDelete() {
    setIsDeleting(true);
    setShowConfirmDialog(false);
    onDelete(id);
  }

  function handleEditCartProduct() {
    onEdit(product);
  }

  return (
    <React.Fragment>
      <div
        className={`cart_product ${isDeleting ? 'dropping' : ''}`}
        id={`cart_product_${id}`}
        style={{ order: id * -1 }}
        data-testid={`cart-product-${id}`}
      >
        <div className="cart_product_container">
          <CartProductImage
            id={id}
            index={index}
            thumbUrl={product.thumbnail_url}
            photoFilenames={photoFilenames}
            canvasColor={borderColor}
            onShowImageGallery={onShowImageGallery}
          />

          <div className="cart_product_table_wrapper">
            <table className="cart_product_table">
              <tbody>
                <tr>
                  <td width="320">
                    <h2
                      className="product_title"
                      dangerouslySetInnerHTML={{ __html: productTitle }}
                    />
                  </td>
                </tr>
                {lineItems}

                {product.pricing.discount > 0 && (
                  <React.Fragment>
                    <tr className="branding" aria-label="item unit price">
                      <td width="320" />
                      <td width="80">Item Price:</td>
                      <td width="96" className="li_price">
                        <span className="price_prefix">$</span>
                        <span>{formatPrice(product.pricing.unit_price, { symbol: '' })}</span>
                      </td>
                    </tr>
                    <tr className="branding" aria-label="item discount">
                      <td width="320" />
                      <td width="80">Discount:</td>
                      <td width="96" className="li_price">
                        <span className="price_prefix">$</span>
                        <span>
                          -
                          {formatPrice(
                            product.pricing.unit_price - product.pricing.unit_price_after_discount,
                            { symbol: '' }
                          )}
                        </span>
                      </td>
                    </tr>
                  </React.Fragment>
                )}

                <tr className="branding" aria-label="item subtotal">
                  <td width="320" />
                  <td width="80">Subtotal:</td>
                  <td width="96" className="li_price">
                    <span className="price_prefix">$</span>{' '}
                    {formatPrice(product.pricing.unit_price_after_discount, { symbol: '' })}
                  </td>
                </tr>
                <tr>
                  <td width="320" />
                  <td width="80">Quantity:</td>
                  <td width="96" className="quantity_select">
                    {allowEdit && (
                      <CartProductQuantityInput
                        id={id}
                        quantity={quantity}
                        onChange={(value) => changeCartProductQuantity(id, value)}
                      />
                    )}
                    {!allowEdit && quantity}
                  </td>
                </tr>
                <tr aria-label="item total">
                  <td width="320">
                    {allowEdit && (
                      <div className="cart_actions">
                        <Button
                          type="bare"
                          className="edit_item"
                          id={`edit-cart-product-${id}`}
                          onClick={handleEditCartProduct}
                        >
                          <FontAwesomeIcon icon="edit" />
                          Edit <span className="screen-reader-text">{product.name}</span>
                        </Button>
                        <Button
                          type="bare"
                          className="remove_item"
                          id={`delete-cart-product-${id}`}
                          onClick={() => setShowConfirmDialog(true)}
                        >
                          <FontAwesomeIcon data-id={id} icon="trash-alt" />
                          Remove <span className="screen-reader-text">{product.name}</span>
                        </Button>
                      </div>
                    )}
                  </td>
                  <td width="80" className="quantity_price_label branding">
                    Total:
                  </td>
                  <td width="96" className="quantity_price branding" data-testid="cp-total">
                    <span className="price_prefix">$</span>
                    <span className="price">
                      {formatPrice(product.pricing.total, { symbol: '' })}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showConfirmDialog && (
        <Alert
          title="Confirm Delete Product"
          labelledBy="confirm-delete-product-modal"
          lastFocusedElementId={`delete-cart-product-${id}`}
          onClose={() => setShowConfirmDialog(false)}
          message="Are you sure you want to delete this item?"
          onContinue={handleDelete}
          onCancel={() => setShowConfirmDialog(false)}
        />
      )}
    </React.Fragment>
  );
}

export default CartProductItem;
